import { useState, useEffect } from 'react';
import { Menu, X, ChevronRight } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pay-next-gen/artifacts/vywq6zhw_BLACKLOGO.png";
const ICON_URL = "https://customer-assets.emergentagent.com/job_pay-next-gen/artifacts/1oy2wqev_ICON.png";

const navLinks = [
  { label: 'Problem', href: '#problem' },
  { label: 'Solution', href: '#solution' },
  { label: 'Features', href: '#features' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'For Who', href: '#audience' },
  { label: 'Traction', href: '#traction' },
  { label: 'About', href: '#about' },
  { label: 'Sandbox', href: '#sandbox' },
  { label: 'Docs', href: '#docs' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        scrolled
          ? 'bg-black/95 backdrop-blur-xl border-b border-[#1A1A1A]'
          : 'bg-transparent'
      }`}
    >
      {/* Scroll progress bar */}
      <div
        data-testid="scroll-progress"
        className="absolute top-0 left-0 h-[2px] bg-[#00FF00] transition-all duration-150"
        style={{ width: `${scrollProgress}%`, boxShadow: '0 0 8px rgba(0,255,0,0.6)' }}
      />

      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#hero"
          onClick={(e) => scrollTo(e, '#hero')}
          data-testid="nav-logo"
          className="flex items-center gap-3"
        >
          <img src={ICON_URL} alt="NX" className="h-10 w-10 object-contain" />
          <span className="hidden sm:block text-white font-bold text-sm uppercase tracking-widest" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            NexGenPay
          </span>
        </a>

        {/* Desktop Nav Links */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => scrollTo(e, link.href)}
              data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
              className="text-[#A3A3A3] hover:text-[#00FF00] text-xs font-medium uppercase tracking-widest transition-colors duration-200 relative group"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#00FF00] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </div>

        {/* CTA */}
        <a
          href="#demo"
          onClick={(e) => scrollTo(e, '#demo')}
          data-testid="nav-cta-btn"
          className="hidden lg:flex items-center gap-2 bg-[#00FF00] text-black text-xs font-bold uppercase tracking-widest px-6 py-3 hover:bg-white hover:shadow-[0_0_20px_rgba(0,255,0,0.5)] transition-all duration-300"
          style={{ fontFamily: 'Unbounded, sans-serif' }}
        >
          Request Demo <ChevronRight size={14} />
        </a>

        {/* Mobile toggle */}
        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-white p-2 hover:text-[#00FF00] transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden bg-black border-t border-[#1A1A1A] overflow-hidden transition-all duration-300 ${
          mobileOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-5">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => scrollTo(e, link.href)}
              className="text-[#A3A3A3] hover:text-[#00FF00] text-sm font-medium uppercase tracking-widest transition-colors duration-200"
            >
              {link.label}
            </a>
          ))}
          <a
            href="#demo"
            onClick={(e) => scrollTo(e, '#demo')}
            data-testid="mobile-cta-btn"
            className="bg-[#00FF00] text-black text-sm font-bold uppercase tracking-widest px-6 py-3 text-center mt-2 hover:bg-white transition-colors"
            style={{ fontFamily: 'Unbounded, sans-serif' }}
          >
            Request Demo
          </a>
        </div>
      </div>
    </nav>
  );
}
